import type { QueryClient } from '@tanstack/react-query';
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Layout } from './components/layout';
import { Alert } from './components/ui/alert';
import { errorMessage } from './lib/api';
import { ReauthDialog, can, meQuery } from './lib/session';
import { AttributesPage } from './pages/attributes';
import { BrandsPage } from './pages/brands';
import { CategoriesPage } from './pages/categories';
import { DashboardPage } from './pages/dashboard';
import { ImportRunPage } from './pages/imports/run';
import { LoginPage } from './pages/login';
import { MatchQueuePage } from './pages/match-queue';
import { PriceApprovalsPage } from './pages/price-approvals';
import { AccountingPage } from './pages/accounting';
import { AdsPage } from './pages/ads';
import { AnalyticsPage } from './pages/analytics';
import { ExperimentsPage } from './pages/experiments';
import { PromotionsPage } from './pages/promotions';
import { ReviewsPage } from './pages/reviews';
import { SeoPage } from './pages/seo';
import { AiPage } from './pages/ai';
import { AiDraftsPage } from './pages/ai-drafts';
import { AssistantPage } from './pages/assistant';
import { ContentPage } from './pages/content';
import { SearchPage } from './pages/search';
import { AuditPage } from './pages/audit';
import { CustomerDetailPage, CustomersPage } from './pages/customers';
import { IntegrationsPage } from './pages/integrations';
import { InvoicesPage } from './pages/invoices';
import { OrderDetailPage } from './pages/orders/detail';
import { OrderListPage } from './pages/orders/list';
import { SegmentsPage } from './pages/segments';
import { SettingsPage } from './pages/settings';
import { PricingRulesPage } from './pages/pricing-rules';
import { SourcingRulesPage } from './pages/sourcing-rules';
import { StaffPage } from './pages/staff';
import { PurchaseOrdersPage } from './pages/purchase-orders';
import { ReturnsPage } from './pages/returns';
import { StockDocumentsPage } from './pages/warehouse/documents';
import { FulfillmentPage } from './pages/warehouse/fulfillment';
import { InboundPage } from './pages/warehouse/inbound';
import { StockPage } from './pages/warehouse/stock';
import { ProductCreatePage, ProductEditPage } from './pages/products/editor';
import { ProductListPage } from './pages/products/list';
import { SupplierDetailPage } from './pages/suppliers/detail';
import { SupplierListPage } from './pages/suppliers/list';

interface RouterContext {
  queryClient: QueryClient;
}

function NotFound() {
  const { t } = useTranslation();
  return <p className="p-6 text-muted-foreground">{t('common.notFound')}</p>;
}

function RouteError({ error }: { error: unknown }) {
  const { t } = useTranslation();
  return (
    <Alert variant="destructive" title={t('common.errorTitle')} className="m-6">
      {errorMessage(error)}
    </Alert>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <>
      <Outlet />
      <ReauthDialog />
    </>
  ),
  notFoundComponent: NotFound,
  errorComponent: RouteError,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

/** Раздел с входом: без действующей сессии (с пройденной 2FA) — на страницу входа. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: Layout,
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.query({ ...meQuery, staleTime: 'static' });
    } catch {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- так TanStack Router делает редирект
      throw redirect({ to: '/login' });
    }
  },
});

/** Страница только для сотрудников с правом; остальным — сообщение, а не ошибки API. */
function guarded(permission: string) {
  return async ({ context }: { context: RouterContext }) => {
    const me = await context.queryClient.query({ ...meQuery, staleTime: 'static' });
    if (!can(me, permission)) throw new Error('forbidden');
  };
}

function Forbidden() {
  const { t } = useTranslation();
  return <Alert variant="warning">{t('common.noAccess')}</Alert>;
}

const page = <TPath extends string>(
  path: TPath,
  permission: string | undefined,
  component: () => React.ReactNode,
) =>
  createRoute({
    getParentRoute: () => appRoute,
    path,
    component,
    ...(permission ? { beforeLoad: guarded(permission), errorComponent: Forbidden } : {}),
  });

const productEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/products/$productId',
  beforeLoad: guarded('catalog.read'),
  errorComponent: Forbidden,
  component: function ProductEditRoute() {
    const { productId } = productEditRoute.useParams();
    return <ProductEditPage productId={productId} />;
  },
});

const supplierDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/suppliers/$supplierId',
  beforeLoad: guarded('suppliers.read'),
  errorComponent: Forbidden,
  component: function SupplierDetailRoute() {
    const { supplierId } = supplierDetailRoute.useParams();
    return <SupplierDetailPage supplierId={supplierId} />;
  },
});

const orderDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/orders/$orderId',
  beforeLoad: guarded('orders.read'),
  errorComponent: Forbidden,
  component: function OrderDetailRoute() {
    const { orderId } = orderDetailRoute.useParams();
    return <OrderDetailPage orderId={orderId} />;
  },
});

const customerDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/customers/$customerId',
  beforeLoad: guarded('customers.read'),
  errorComponent: Forbidden,
  component: function CustomerDetailRoute() {
    const { customerId } = customerDetailRoute.useParams();
    return <CustomerDetailPage customerId={customerId} />;
  },
});

const importRunRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/imports/$runId',
  beforeLoad: guarded('imports.run'),
  errorComponent: Forbidden,
  component: function ImportRunRoute() {
    const { runId } = importRunRoute.useParams();
    return <ImportRunPage runId={runId} />;
  },
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  appRoute.addChildren([
    page('/', undefined, DashboardPage),
    page('/products', 'catalog.read', ProductListPage),
    page('/products/new', 'catalog.write', ProductCreatePage),
    productEditRoute,
    page('/categories', 'catalog.read', CategoriesPage),
    page('/brands', 'catalog.read', BrandsPage),
    page('/attributes', 'catalog.read', AttributesPage),
    page('/suppliers', 'suppliers.read', SupplierListPage),
    supplierDetailRoute,
    importRunRoute,
    page('/purchase-orders', 'suppliers.read', PurchaseOrdersPage),
    page('/orders', 'orders.read', OrderListPage),
    orderDetailRoute,
    page('/customers', 'customers.read', CustomersPage),
    customerDetailRoute,
    page('/segments', 'customers.read', SegmentsPage),
    page('/ads', 'analytics.read', AdsPage),
    page('/analytics', 'analytics.read', AnalyticsPage),
    page('/promotions', 'promotions.manage', PromotionsPage),
    page('/reviews', 'reviews.moderate', ReviewsPage),
    page('/experiments', 'analytics.read', ExperimentsPage),
    page('/search', 'search.manage', SearchPage),
    page('/ai', 'settings.manage', AiPage),
    page('/content', 'content.manage', ContentPage),
    page('/seo', 'seo.manage', SeoPage),
    page('/assistant', 'ai.assistant', AssistantPage),
    page('/ai/drafts', 'ai.content_review', AiDraftsPage),
    page('/sourcing-rules', 'sourcing.manage', SourcingRulesPage),
    page('/settings', 'settings.manage', SettingsPage),
    page('/staff', 'users.manage', StaffPage),
    page('/audit', 'audit.read', AuditPage),
    page('/integrations', 'integrations.read', IntegrationsPage),
    page('/returns', 'returns.read', ReturnsPage),
    page('/invoices', 'invoices.read', InvoicesPage),
    page('/accounting', 'invoices.export', AccountingPage),
    page('/warehouse/stock', 'inventory.read', StockPage),
    page('/warehouse/inbound', 'inventory.read', InboundPage),
    page('/warehouse/fulfillment', 'fulfillment.ship', FulfillmentPage),
    page('/warehouse/documents', 'inventory.read', StockDocumentsPage),
    page('/match-queue', 'imports.run', MatchQueuePage),
    page('/price-approvals', 'pricing.approve', PriceApprovalsPage),
    page('/pricing', 'pricing.read', PricingRulesPage),
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    // Данные кэширует TanStack Query — роутер их не дублирует.
    defaultPreloadStaleTime: 0,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
